import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { MetricCard, GlassCard, PageTitle, SkeletonGrid, LuxuryButton } from "@/components/lux";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserCheck, UserPlus, Network, Sparkles, Wallet, ArrowDownToLine, TrendingUp, Trophy, Banknote, PlayCircle } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function AdminDashboard() {
  const { data, loading } = useFetch("/admin/dashboard");
  const [busy, setBusy] = useState(false);
  const runDaily = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/process-daily"); toast.success(r.data.message); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  if (loading || !data) return <><PageTitle title="Admin Dashboard" /><SkeletonGrid /></>;
  return (
    <div className="space-y-6">
      <PageTitle title="Admin Dashboard" subtitle="Platform overview" right={
        <LuxuryButton size="sm" onClick={runDaily} disabled={busy} data-testid="run-daily"><PlayCircle size={16} className="mr-1 inline" /> Process Daily Salary/ROI</LuxuryButton>
      } />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Users" value={data.total_users} icon={Users} tone="royal" />
        <MetricCard label="Active Users" value={data.active_users} icon={UserCheck} tone="gold" />
        <MetricCard label="Pending Users" value={data.pending_users} icon={UserPlus} tone="maroon" />
        <MetricCard label="Matched Business" value={`$${money(data.matched_volume)}`} icon={Network} tone="royal" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Real Volume" value={`$${money(data.real_volume)}`} icon={TrendingUp} tone="royal" />
        <MetricCard label="Promotional Volume" value={`$${money(data.virtual_volume)}`} icon={Sparkles} tone="maroon" />
        <MetricCard label="Commission Liability" value={`$${money(data.commission_liability)}`} icon={Wallet} tone="gold" />
        <MetricCard label="Salary Accrued" value={`$${money(data.salary_accrued)}`} icon={Banknote} tone="white" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Pending Deposits" value={data.pending_deposits} icon={Wallet} tone="maroon" />
        <MetricCard label="Approved Deposits" value={`$${money(data.approved_deposits)}`} icon={Wallet} tone="royal" />
        <MetricCard label="Pending Withdrawals" value={data.pending_withdrawals} icon={ArrowDownToLine} tone="gold" />
        <MetricCard label="Rewards Issued" value={`$${money(data.rewards_issued)}`} icon={Trophy} tone="gold" />
      </div>
      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">User Growth</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.growth_series}>
              <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7c6bf5" stopOpacity={0.5} /><stop offset="100%" stopColor="#7c6bf5" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" stroke="#97a0b5" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#0b0e17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Area type="monotone" dataKey="users" stroke="#7c6bf5" fill="url(#ag)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
    </div>
  );
}
